import { useEffect, useMemo, useRef, useState } from 'react';
import ArtworkIcon from "src/assets/all-icons/artwork-icon.jsx";

import DropdownArrowIcon from '@/assets/all-icons/dropdown-arrow-icon';
import config from '@/config';
import useNavigateToArtwork from "@/hooks/useNavigateToArtwork";
import useQuery from "@/hooks/useQuery";
import useTranslations from '@/i18n/useTranslations';
import useSearchRandomizedFeed from '@/pages/search/hooks/useSearchRandomizedFeed';
import fetchWrapper from '@/services/fetchWrapper.service';
import HomeHeader from '@/shared-components/home/components/HomeHeader';
import Loader from '@/shared-components/loader/Loader';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import Text from "@/shared-components/text/components/Text";
import AppNavigationPage from '@/shared-components/wrappers/components/AppNavigationPage';
import { getSsrSearchLandingBootstrap } from '@/utils/ssrSearchLandingBootstrap';

import FiltersRenderer from "./components/FiltersRenderer";
import SearchFilters from './components/search-filters';
import VirtualList from './components/VirtualList';

import './index.css';

const STORAGE_KEYS = {
  general: 'homeArtworksQuery',
  search: 'homeArtworksQuery_search',
};
const SEARCH_INITIAL_QUERY = { q: "" };
const HOME_RANDOM_ENDPOINT = `${config.apis.api.url}/home/links/randomized/artworks`;
const RANDOM_LIMIT = 32;

function readInitialSearchLandingSeoPayload() {
  const payload = getSsrSearchLandingBootstrap('artworks');
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

function sanitizeArtworkSearchQuery(rawQuery) {
  const source = rawQuery && typeof rawQuery === "object" ? rawQuery : {};
  const rest = { ...source };
  delete rest.sort;
  return rest;
}

function HomeArtworksRandomized() {
  const tSearchCategories = useTranslations('common.shared.search.categories');
  const [searchSeo, setSearchSeo] = useState(() => readInitialSearchLandingSeoPayload());
  const [state, setState] = useState({
    loading: true,
    loadingMore: false,
    data: [],
    error: null,
    exhausted: false,
  });
  const requestIdRef = useRef(0);

  const dedupeArtworks = (current = [], incoming = []) => {
    const next = [...current];
    const seen = new Set(
      current
        .map((item) => item?._id || item?.id || item?.slug)
        .filter(Boolean)
        .map((value) => String(value))
    );

    incoming.forEach((item) => {
      const key = item?._id || item?.id || item?.slug;
      if (!key) {
        return;
      }

      const normalizedKey = String(key);
      if (seen.has(normalizedKey)) {
        return;
      }

      seen.add(normalizedKey);
      next.push(item);
    });

    return next;
  };

  const fetchRandomBatch = async () => {
    const { response, data } = await fetchWrapper.get(
      `${HOME_RANDOM_ENDPOINT}?artwork_limit=${RANDOM_LIMIT}`,
      { optionalToken: true }
    );

    if (!response.ok || !Array.isArray(data)) {
      return [];
    }

    return data;
  };

  useEffect(() => {
    let ignore = false;

    async function loadSearchSeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/search/artworks`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setSearchSeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load artworks SEO payload', error);
        }
      }
    }

    loadSearchSeo();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    const loadInitialData = async () => {
      setState((current) => ({ ...current, loading: true, error: null }));

      try {
        const batch = await fetchRandomBatch();
        if (cancelled || requestIdRef.current !== requestId) {
          return;
        }

        const pool = dedupeArtworks([], batch);
        setState({
          loading: false,
          loadingMore: false,
          data: pool,
          error: pool.length === 0 ? "No artworks available at the moment." : null,
          exhausted: pool.length === 0,
        });
      } catch (error) {
        if (cancelled || requestIdRef.current !== requestId) {
          return;
        }

        setState({
          loading: false,
          loadingMore: false,
          data: [],
          error: error?.message || "Unable to refresh artworks at the moment.",
          exhausted: true,
        });
      }
    };

    void loadInitialData();

    return () => {
      cancelled = true;
    };
  }, []);

  const randomizedItems = useMemo(
    () => ({
      data: state.data,
      totalDocs: state.exhausted ? state.data.length : state.data.length + 1,
    }),
    [state.data, state.exhausted]
  );
  const navigateToArtwork = useNavigateToArtwork(
    "home_artworks_random",
    randomizedItems,
    `${HOME_RANDOM_ENDPOINT}?artwork_limit=${RANDOM_LIMIT}`,
    { type: 'home-artworks', label: 'Artworks' }
  );

  const loadMoreData = async () => {
    if (state.loading || state.loadingMore || state.exhausted) {
      return;
    }

    const requestId = requestIdRef.current;
    setState((current) => ({ ...current, loadingMore: true, error: null }));

    try {
      const batch = await fetchRandomBatch();
      if (requestIdRef.current !== requestId) {
        return;
      }

      setState((current) => {
        const merged = dedupeArtworks(current.data, batch);
        return {
          ...current,
          loading: false,
          loadingMore: false,
          data: merged,
          exhausted: merged.length === current.data.length,
          error: merged.length === current.data.length
            ? "No more artworks available at the moment."
            : null,
        };
      });
    } catch (error) {
      if (requestIdRef.current !== requestId) {
        return;
      }

      setState((current) => ({
        ...current,
        loading: false,
        loadingMore: false,
        error: error?.message || "Unable to load more artworks at the moment.",
      }));
    }
  };

  return (
    <>
      <StaticSeoHead
        title={searchSeo?.title || "Original Artworks for Sale | Suarte"}
        description={searchSeo?.metaDescription || "Discover original paintings for sale on Suarte. Explore curated works by represented artists and fine art galleries from around the world."}
        canonicalUrl={searchSeo?.canonicalUrl}
        canonicalPath={searchSeo?.canonicalPath || "/artworks"}
        robots={searchSeo?.robots}
        ogType={searchSeo?.ogType}
        image={searchSeo?.ogImage}
        structuredData={searchSeo?.structuredData}
        pageType="CollectionPage"
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: 'Artworks', url: 'https://suarte.art/artworks' },
        ]}
      />
      <AppNavigationPage>
        <div className="home-artworks__page home-artworks__randomized">
          <HomeHeader
            onInput={() => {}}
            value=""
            icon={<ArtworkIcon className="home-artworks__header-icon" />}
            text={tSearchCategories('artworks')}
            type="artworks"
            placeholder="Search for artworks..."
            hideSearch
          />
          {state.loading ? (
            <Loader />
          ) : randomizedItems?.data?.length === 0 ? (
            <Text className="home-artworks__empty-text font-size-medium" paragraph>
              {state.error || "No artworks available at the moment."}
            </Text>
          ) : (
            <VirtualList
              items={randomizedItems}
              onLoadMore={loadMoreData}
              navigateToArtwork={navigateToArtwork}
            />
          )}
        </div>
      </AppNavigationPage>
    </>
  );
}

function HomeArtworksSearch({ fromSearch = false, valueFromSearch = "" }) {
  useTranslations('common.shared.search');
  const [openFilters, setOpenFilters] = useState(false);
  const originalObject = SEARCH_INITIAL_QUERY;
  const storageKey = fromSearch ? STORAGE_KEYS.search : STORAGE_KEYS.general;
  const [query, setQuery] = useState(() => {
    const stored = readStoredQuery(storageKey);
    if (stored) {
      return sanitizeArtworkSearchQuery(stored);
    }
    return originalObject;
  });

  const hasActiveFilters = (currentQuery) => {
    return Object.keys(currentQuery).some((key) => {
      if (key === 'q' || key === 'sort') {
        return false;
      }

      const value = currentQuery[key];
      if (!value) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') return Object.keys(value).length > 0;
      return true;
    });
  };

  const hasSearchText = Boolean(valueFromSearch?.q);
  const hasFiltersApplied = hasActiveFilters(query);
  const hasSearchContext = hasSearchText || hasFiltersApplied;
  const shouldUseRandomized = fromSearch && !hasSearchContext;

  const { loading, queryData, loadMoreData } = useQuery("home_artworks", `${config.apis.api.url}/search/artwork`, query, {
    optionalToken: true,
    customKeyGetter: {
      artist: (currentValue) => currentValue.map(item => item._id),
      gallery: (currentValue) => currentValue.map(item => item._id),
      price: (currentValue) => currentValue ? [currentValue.minPrice, currentValue.maxPrice === 50000 ? Number.MAX_SAFE_INTEGER : currentValue.maxPrice] : null,
    },
    customQueryValidation: () => !shouldUseRandomized,
  });
  
  const navigateToArtwork = useNavigateToArtwork(
    `home_artworks`,
    queryData?.data,
    `/search/artwork${queryData?.queryString}`,
    { type: 'home-artworks', label: 'Artworks' }
  );

  const randomizedState = useSearchRandomizedFeed({
    feedKey: 'artworks',
    endpoint: HOME_RANDOM_ENDPOINT,
    limitParam: 'artwork_limit',
    limit: RANDOM_LIMIT,
    enabled: shouldUseRandomized,
    errorMessage: 'Unable to refresh artworks at the moment.',
  });

  const navigateToRandomArtwork = useNavigateToArtwork(
    "home_artworks_random_search",
    { data: randomizedState.data },
    `${HOME_RANDOM_ENDPOINT}?artwork_limit=${RANDOM_LIMIT}`,
    { type: 'home-artworks', label: 'Artworks' }
  );

  const randomizedItems = useMemo(
    () => ({
      data: randomizedState.data,
      totalDocs: randomizedState.data.length,
    }),
    [randomizedState.data]
  );

  const checkIfPropertyAdded = (originalObj, updatedObj) => {
    const originalKeys = Object.keys(originalObj);
    const updatedKeys = Object.keys(updatedObj);
    return updatedKeys.filter(key => !originalKeys.includes(key));
  };

  const checkIfEmptyProperties = (properties) => {
    return properties.filter(key => {
      const value = query[key];
      return (!value || Array.isArray(value) && value.length === 0) || 
             (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) || 
             (value === "" || value === false);
    });
  };

  useEffect(() => {
    const addedKeys = checkIfPropertyAdded(originalObject, query);
    const emptyProps = checkIfEmptyProperties(addedKeys);

    emptyProps.forEach(key => {
      if (key in query) {
        delete query[key];
      }
    });
  }, [query]);

  useEffect(() => {
    writeStoredQuery(storageKey, query);
  }, [query, storageKey]);

  function onInput(value) {
    const sanitizedValue = value ?? '';
    const newQuery = { ...query, q: sanitizedValue };
    setQuery(newQuery);
  }

  const updateQuery = (newQuery) => {
    setQuery(cleanQuery(newQuery));
  };

  function cleanQuery(obj) {
    return sanitizeArtworkSearchQuery(Object.fromEntries(
      Object.entries(obj).filter(([, value]) => !(Array.isArray(value) && value.length === 0))
    ));
  }

  useEffect(() => {
    onInput(valueFromSearch.q);
  }, [valueFromSearch.q]);

  if (shouldUseRandomized) {
    return (
      <div className="home-artworks__page">
        <SearchFilters
          query={query}
          setQuery={updateQuery}
          open={openFilters}
          close={()=>setOpenFilters(false)}
        />
        <div className="home-header__popup-container-fromSearch">
          <Text className="home-artworks__filters" paragraph onClick={()=>setOpenFilters(true)}>
            Filters <DropdownArrowIcon/>
          </Text>
          <FiltersRenderer
            query={query}
          />
        </div>
        { randomizedState.loading ? (
          <Loader />
        ) : randomizedItems.data.length === 0 ? (
          <Text className="home-artworks__empty-text font-size-medium" paragraph>
            {randomizedState.error || "No artworks available at the moment."}
          </Text>
        ) : (
          <VirtualList
            items={randomizedItems}
            onLoadMore={() => {}}
            navigateToArtwork={navigateToRandomArtwork}
            fromSearch={fromSearch}
          />
        )}
      </div>
    );
  }

  return (
    <div className="home-artworks__page">
      <SearchFilters 
        query={query} 
        setQuery={updateQuery} 
        open={openFilters} 
        close={()=>setOpenFilters(false)}
      />
      {hasSearchContext ? (
        <div className="home-artworks__search-summary">
          <div className="home-artworks__filters-flex">
            <Text className="home-artworks__filters" paragraph onClick={()=>setOpenFilters(true)}>
              Filters <DropdownArrowIcon/>
            </Text>

            <FiltersRenderer
              query={query}
            />
          </div>

          {!loading && (
            <Text className="home-artworks__filters-with-search" paragraph>
              {queryData.data.totalDocs} results {valueFromSearch.q && <>for <span>{valueFromSearch.q}</span></>}
            </Text>
          )}
        </div>
      ) : !loading ? (
        <div className="home-header__popup-container-fromSearch">
          <Text className="home-artworks__filters" paragraph onClick={()=>setOpenFilters(true)}>
            Filters <DropdownArrowIcon/>
          </Text>

          <FiltersRenderer
            query={query}
          />
        </div>
      ) : null}
      { loading ? (
        <Loader />
      ) : queryData.data.data.length === 0 ? (
        <Text className="home-artworks__empty-text font-size-medium" paragraph>
          No artworks available for your search query.
        </Text>
      ) : (
        <VirtualList
          key={queryData.queryString}
          items={queryData.data}
          onLoadMore={loadMoreData}
          navigateToArtwork={navigateToArtwork}
          fromSearch={fromSearch}
        />
      )}
    </div>
  );
}

function readStoredQuery(key) {
  if (!key || typeof window === 'undefined') {
    return null;
  }

  try {
    const storedValue = window.sessionStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : null;
  } catch (_) {
    return null;
  }
}

function writeStoredQuery(key, value) {
  if (!key || typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (_) {
    // ignore write errors (private mode / disabled storage)
  }
}

export default function HomeArtworks(props) {
  if (props.fromSearch) {
    return <HomeArtworksSearch {...props} />;
  }
  return <HomeArtworksRandomized />;
}
